/**
 * DataNoteBadge — surfaces why a fiscal year's numbers were adjusted or estimated.
 *
 * The pipeline routinely restates, estimates, or gives up on a year's data. Until
 * now those events lived only in server logs, so a corrected number simply changed
 * with no explanation. This badge makes the reason visible at the point of use.
 *
 * Motivating case: Nestlé India's 1:10 split (2024-01-05) meant pre-split years were
 * priced with a post-split share count, overstating market cap — and therefore
 * EV/EBITDA — by 10x. That is fixed, but a silent fix is its own transparency problem.
 *
 * Reuses ui/Badge (already forwardRef'd to act as a Tooltip trigger) and ui/Tooltip.
 * Note ui/Tooltip is hover/focus-only and pointer-events-none, so note content must
 * stay read-only — no links or copyable text.
 */
import React from 'react';
import { AlertTriangle, Info, PencilLine, XCircle } from 'lucide-react';
import Tooltip from '../ui/Tooltip';
import { Badge } from '../ui/Badge';
import type { BadgeProps } from '../ui/Badge';
import type { DataNote, NoteSeverity } from '../../types/financial';

/** Ranked worst-first: a year's badge reflects its most severe note. */
const SEVERITY_ORDER: NoteSeverity[] = ['error', 'warning', 'adjustment', 'info'];

const SEVERITY_STYLE: Record<
  NoteSeverity,
  { variant: BadgeProps['variant']; Icon: typeof Info; label: string }
> = {
  error:      { variant: 'error',   Icon: XCircle,       label: 'Unreliable data' },
  warning:    { variant: 'warning', Icon: AlertTriangle, label: 'Estimated data' },
  adjustment: { variant: 'info',    Icon: PencilLine,    label: 'Adjusted data' },
  info:       { variant: 'neutral', Icon: Info,          label: 'Data note' },
};

/** Human-readable names for the DTO field keys a note lists as affected. */
const FIELD_LABELS: Record<string, string> = {
  market_cap: 'Market cap',
  enterprise_value: 'Enterprise value',
  ev_ebitda: 'EV/EBITDA',
  wacc: 'WACC',
  wacc_percentage: 'WACC',
  ev_from_ebit: 'EV from EBIT',
  cap: 'CAP',
};

function highestSeverity(notes: DataNote[]): NoteSeverity {
  return (
    SEVERITY_ORDER.find((s) => notes.some((n) => n.severity === s)) ?? 'info'
  );
}

/**
 * Renders note bodies. Shared by both note surfaces — the badge's ui/Tooltip
 * (`light` variant) and the charts' custom tooltip cards — which are both white
 * cards, so the same note looks the same wherever it is hovered.
 *
 * Text colour is inherited from the container and shaded with relative opacity
 * rather than fixed colours, so it survives a container restyle.
 */
export function DataNoteList({ notes }: { notes: DataNote[] }) {
  return (
    <div className="space-y-2 text-left">
      {notes.map((note, i) => {
        const { Icon } = SEVERITY_STYLE[note.severity] ?? SEVERITY_STYLE.info;
        const affected = (note.fields_affected ?? [])
          .map((f) => FIELD_LABELS[f] ?? f)
          // the same label can map from several keys (wacc / wacc_percentage)
          .filter((label, idx, all) => all.indexOf(label) === idx);
        return (
          <div
            key={note.code + i}
            className={i > 0 ? 'pt-2 border-t border-neutral-200' : ''}
          >
            <div className="flex items-start gap-1.5 font-semibold">
              <Icon className="w-3.5 h-3.5 mt-px shrink-0" aria-hidden="true" />
              <span>{note.title}</span>
            </div>
            <p className="mt-1 leading-snug opacity-90">{note.message}</p>
            {affected.length > 0 && (
              <p className="mt-1 opacity-75">
                <span className="font-medium">Affects:</span> {affected.join(', ')}
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
}

/**
 * The em dash shown where a metric was deliberately not calculated, carrying the
 * reason on hover.
 *
 * The backend now writes null for a metric it cannot derive from observed data
 * instead of estimating one — an unpriced year used to get a share price implied
 * from another year's P/E, which then rendered indistinguishably from a real
 * close. A bare '—' only trades a wrong number for an unexplained blank, so the
 * note that travels with it is surfaced right on the cell.
 *
 * Returns a plain '—' when no note explains this field, so callers can use it
 * for every empty cell without checking first.
 */
export function SuppressedValue({
  notes,
  field,
  fallbackNote,
}: {
  notes?: DataNote[];
  field: string;
  /**
   * Note to explain the blank when the year carries none of its own for this
   * field — e.g. the engine-wide "no market price" suppression on PDF-ingested
   * companies, whose nulls arrive without per-year notes.
   */
  fallbackNote?: DataNote;
}) {
  let relevant = (notes ?? []).filter((n) => (n.fields_affected ?? []).includes(field));
  if (relevant.length === 0 && fallbackNote) relevant = [fallbackNote];
  if (relevant.length === 0) return <span className="text-neutral-400">—</span>;

  return (
    <Tooltip content={<DataNoteList notes={relevant} />} position="auto" variant="light" maxWidth={340}>
      <span
        tabIndex={0}
        role="note"
        aria-label={`Not calculated. ${relevant.map((n) => n.title).join('. ')}`}
        className="cursor-help underline decoration-dotted decoration-neutral-400 underline-offset-4 text-neutral-400"
      >
        —
      </span>
    </Tooltip>
  );
}

interface DataNoteBadgeProps {
  notes?: DataNote[];
  /** Show the severity label next to the icon (tables); icon-only when false (axis ticks). */
  showLabel?: boolean;
  className?: string;
}

/**
 * A single badge summarising every note on one year. Renders nothing when the
 * year is clean — the common case, and the absence of a badge is itself signal.
 */
export default function DataNoteBadge({
  notes,
  showLabel = false,
  className = '',
}: DataNoteBadgeProps) {
  if (!notes || notes.length === 0) return null;

  const severity = highestSeverity(notes);
  const { variant, Icon, label } = SEVERITY_STYLE[severity] ?? SEVERITY_STYLE.info;
  const summary = notes.length > 1 ? `${label} (${notes.length})` : label;

  return (
    <Tooltip
      content={<DataNoteList notes={notes} />}
      position="auto"
      // `light` (white card) rather than the default brand lavender, so the badge
      // tooltip reads identically to the charts' note tooltips — the same notes
      // shouldn't change appearance depending on which surface you hover.
      variant="light"
      maxWidth={340}
    >
      <Badge
        variant={variant}
        size="sm"
        tabIndex={0}
        role="note"
        aria-label={`${summary}. ${notes.map((n) => n.title).join('. ')}`}
        className={`cursor-help gap-1 ${className}`}
      >
        <Icon className="w-3 h-3" aria-hidden="true" />
        {showLabel && <span>{summary}</span>}
      </Badge>
    </Tooltip>
  );
}
